import { Component } from "@theme/component"
import { ThemeEvents } from "@theme/events"

/**
 * A custom element that displays a cart icon.
 * Updated to hide bubble numbers and only use red fill effect
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ["cartBubble", "cartBubbleText", "cartBubbleCount"]

  /** @type {number} */
  get currentCartCount() {
    return Number.parseInt(this.refs.cartBubbleCount.textContent ?? "0", 10)
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = String(value)
  }

  connectedCallback() {
    super.connectedCallback()

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate)
    this.ensureCartBubbleIsCorrect()
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate)
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0
    const comingFromProductForm = event.detail.data?.source === "product-form-component"

    this.renderCartBubble(itemCount, comingFromProductForm)
  }

  /**
   * Renders the cart bubble.
   * Hide bubble numbers completely, only toggle red fill class
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    this.refs.cartBubbleCount.classList.add("hidden")
    this.refs.cartBubble.classList.add("visually-hidden")
    this.refs.cartBubble.classList.remove("cart-bubble--animating")

    this.classList.toggle("header-actions__cart-icon--has-cart", itemCount > 0)

    document.querySelectorAll("[data-cart-count]").forEach((btn) => {
      btn.setAttribute("data-cart-count", itemCount)
    })

    document.querySelectorAll(".header-actions__cart-button .svg-wrapper").forEach((wrapper) => {
      if (itemCount > 0) {
        wrapper.classList.add("has-items")
      } else {
        wrapper.classList.remove("has-items")
      }
    })

    sessionStorage.setItem(
      "cart-count",
      JSON.stringify({
        value: String(itemCount),
        timestamp: Date.now(),
      }),
    )
  }

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem("cart-count")

    if (sessionStorageCount === null) return

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount)

      if (Date.now() - timestamp < 10000) {
        const count = Number.parseInt(value, 10)

        if (count >= 0) {
          this.renderCartBubble(count, false, false)
        }
      }
    } catch (_) {
      // no-op
    }
  }
}

if (!customElements.get("cart-icon")) {
  customElements.define("cart-icon", CartIcon)
}
